import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FaRegTrashAlt, FaSyncAlt } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  isDefinedString,
  Modal,
  ModalContent,
  ModalFooter,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Text,
  Typography,
} from '@client/toolkit';
import {
  TenantMemberReadModel,
  useDeletePersonsByIdMutation,
  useDeletePersonsInviteByInvitationIdMutation,
  useGetTenantsCurrentMembersQuery,
  usePostPersonsByIdReactivateMutation,
  usePostPersonsInviteByInvitationIdResendMutation,
} from '@client/api';
import { useTenants } from '../../../hooks/useTenants/useTenants';
import { useSession } from '../../../hooks/useSession/useSession';
import { TenantSelectionEnum } from '../../../redux';
import { ProfileUserInviteModal } from '../components/profile-user-invite-modal.component';
import { PermissionBasedActionButton } from '../../../components/permission-based-button/permission-based-button';
import { PermissionKeys } from '@client/api';
import { useUserPermission } from '../../../hooks/useUserPermission/useUserPermission';
interface ProfileUserRowProps {
  user?: TenantMemberReadModel;
  isFetching?: boolean;
  isCurrentUser?: boolean;
  onClickDelete?: (user: TenantMemberReadModel) => void;
  onStatusChange?: (user: TenantMemberReadModel, newStatus: 'Active' | 'Inactive') => void;
  onResendInvite?: (user: TenantMemberReadModel) => void;
}

const ProfileUserRow = ({
  user,
  isFetching,
  isCurrentUser,
  onClickDelete,
  onStatusChange,
  onResendInvite,
}: ProfileUserRowProps) => {
  const { t: tType } = useTranslation('type');
  const isInactive = user?.status === 'Inactive';
  const isInvited = user?.status === 'Invited';

  const { hasPermission } = useUserPermission();
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (user && onStatusChange) {
      onStatusChange(user, e.target.value as 'Active' | 'Inactive');
    }
  };

  return (
    <TableRow className={isInactive ? 'opacity-60' : ''}>
      <TableCell loading={isFetching} className="align-middle">
        <Text isEmpty={!isDefinedString(user?.name)}>
          {user?.name}
        </Text>
      </TableCell>
      <TableCell loading={isFetching} className="align-middle">
        <Text isEmpty={!isDefinedString(user?.email)}>{user?.email}</Text>
      </TableCell>
      <TableCell loading={isFetching} className="align-middle">
        <Text isEmpty={!isDefinedString(user?.culture?.language)}>
          {user?.culture?.language ? tType(`Language.${user.culture.language}`) : '-'}
        </Text>
      </TableCell>
      <TableCell loading={isFetching} className="align-middle">
        <Text isEmpty={!isDefinedString(user?.culture?.timezone)}>{user?.culture?.timezone ?? '-'}</Text>
      </TableCell>
      <TableCell loading={isFetching} className="align-middle">
        <div className="flex items-center gap-2">
          <Text isEmpty={!isDefinedString(user?.role)}>{user?.role ? tType(`UserRole.${user.role}`) : '-'}</Text>
          {isCurrentUser && <span className="text-xs text-brand-primary font-medium">({tType('you')})</span>}
        </div>
      </TableCell>
      <TableCell loading={isFetching} className="align-middle">
        {isInvited ? (
          <div className="flex flex-col">
            <Text className="text-info">{tType('TenantMemberStatus.Invited')}</Text>
            {user?.invitedAt && (
              <Text className="text-xs text-secondary">
                {new Date(user.invitedAt).toLocaleDateString(undefined, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Text>
            )}
          </div>
        ) : isCurrentUser || !hasPermission([PermissionKeys.Tenant.UserUpdate]) ? (
          <Text className="text-secondary">{tType(`TenantMemberStatus.${user?.status ?? 'Active'}`)}</Text>
        ) : (
          <Select
            value={user?.status ?? 'Active'}
            onChange={handleStatusChange}
            disabled={isFetching || !user}
            className={`text-sm h-9 ${isInactive ? 'text-warning' : ''}`}
          >
            <option value="Active">{tType('TenantMemberStatus.Active')}</option>
            <option value="Inactive">{tType('TenantMemberStatus.Inactive')}</option>
          </Select>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap pl-4 align-middle" align="right">
        {isInvited && (
          <div className="flex items-center justify-end gap-2">
            <PermissionBasedActionButton
              size="xs"
              color="custom"
              disabled={!onResendInvite || !user || isFetching}
              onClick={() => (onResendInvite && user ? onResendInvite(user) : undefined)}
              className="text-brand-primary bg-surface-elevated size-9"
              permissions={[PermissionKeys.Tenant.UserInvite]}
            >
              <FaSyncAlt />
            </PermissionBasedActionButton>
            <PermissionBasedActionButton
              color="custom"
              size="xs"
              onClick={() => (onClickDelete && user ? onClickDelete(user) : undefined)}
              disabled={!onClickDelete || !user}
              permissions={[PermissionKeys.Tenant.UserDelete]}
              className="text-brand-primary bg-surface-elevated size-9"
            >
              <FaRegTrashAlt />
            </PermissionBasedActionButton>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

export const ProfileUsers = () => {
  const { t: tGlobal } = useTranslation('global');
  const { t: tCommon } = useTranslation();

  const { selectedTenant } = useTenants();
  const { sessionUser } = useSession();
  const currentUserId = sessionUser?.id;
  const tenant = selectedTenant && selectedTenant !== TenantSelectionEnum.All ? selectedTenant : null;
  const tenantId = tenant?.id;

  const pageSize = 100;
  const [page, setPage] = useState(0);
  const [inviting, setInviting] = useState(false);
  const [deletingUser, setDeletingUser] = useState<TenantMemberReadModel | null>(null);

  // Always include inactive users
  const { data, isLoading, isFetching, refetch } = useGetTenantsCurrentMembersQuery({
    'pagination.Limit': pageSize,
    'pagination.Offset': page * pageSize,
    includeInactive: true,
  });

  const [deleteInvitation, { isLoading: isDeletingInvitation }] = useDeletePersonsInviteByInvitationIdMutation();
  const [deleteUser, { isLoading: isDeletingUser }] = useDeletePersonsByIdMutation();
  const [reactivateUser, { isLoading: isReactivating }] = usePostPersonsByIdReactivateMutation();
  const [resendInvitation, { isLoading: isResending }] = usePostPersonsInviteByInvitationIdResendMutation();

  const isDeleting = isDeletingInvitation || isDeletingUser;
  const isChangingStatus = isReactivating || isDeletingUser || isResending;

  const users = useMemo(() => data?.items ?? [], [data?.items]);
  const hasItems = Array.isArray(users) && users.length > 0;
  const totalCount = data?.totalCount ?? 0;

  const handleOpenDelete = (user: TenantMemberReadModel) => {
    setDeletingUser(user);
  };

  const handleCloseDelete = () => {
    setDeletingUser(null);
  };

  const handleDelete = async () => {
    if (!deletingUser?.id) return;

    try {
      if (deletingUser.status === 'Invited') {
        // Delete invitation
        await deleteInvitation({ invitationId: deletingUser.id }).unwrap();
      } else if (deletingUser.userId) {
        // Delete active user
        await deleteUser({ id: deletingUser.userId }).unwrap();
      } else {
        toast.error(tCommon('errorMissingUserId'));
        return;
      }
      setDeletingUser(null);
      refetch();
    } catch (e) {
      // Error handling
    }
  };

  const handleResendInvite = async (user: TenantMemberReadModel) => {
    if (!user.id) {
      toast.error(tCommon('errorMissingInvitationId'));
      return;
    }

    try {
      await resendInvitation({ invitationId: user.id }).unwrap();
      toast.success(tCommon('invitationResent'));
      refetch();
    } catch (e) {
      // Error handling
    }
  };

  const handleStatusChange = async (user: TenantMemberReadModel, newStatus: 'Active' | 'Inactive') => {
    if (!user.userId) {
      toast.error(tCommon('errorMissingUserId'));
      return;
    }

    // If current status is same as new status, do nothing
    if (user.status === newStatus) return;

    try {
      if (newStatus === 'Inactive') {
        // Deactivate user (soft delete)
        await deleteUser({ id: user.userId }).unwrap();
        toast.success(tCommon('userDeactivated'));
      } else {
        // Reactivate user
        await reactivateUser({ id: user.userId }).unwrap();
        toast.success(tCommon('userReactivated'));
      }
      refetch();
    } catch (e) {
      // Error handling
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle variant="highlighted" className="flex-1">
            {tGlobal('entity.user', { context: 'plural' })}
          </CardTitle>
          <CardActions>
            <PermissionBasedActionButton
              color="primary-dark"
              size="sm"
              onClick={() => setInviting(true)}
              disabled={isFetching || !tenantId}
              permissions={[PermissionKeys.Tenant.UserInvite]}
              title={tCommon('invite')}
            />
          </CardActions>
        </CardHeader>
        <CardContent variant="fill" className="rounded-lg w-full px-4 pb-4">
        <Table className="overflow-hidden w-full mb-4 rounded-md" size="xs" layout="fixed">
          {!isLoading && !hasItems && (
            <caption className="px-4 py-4 [caption-side:bottom]">
              <EmptyPlaceholder>
                {tCommon('noEntityFound', { entity: tGlobal('entity.user', { context: 'plural' }) })}
              </EmptyPlaceholder>
            </caption>
          )}

          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{tGlobal('user.name')}</TableHeadCell>
              <TableHeadCell>{tGlobal('user.email')}</TableHeadCell>
              <TableHeadCell>{tGlobal('user.language')}</TableHeadCell>
              <TableHeadCell>{tGlobal('user.timezone')}</TableHeadCell>
              <TableHeadCell>{tGlobal('user.role')}</TableHeadCell>
              <TableHeadCell>{tGlobal('user.status')}</TableHeadCell>
              <TableHeadCell className="w-[1%]" align="right">
                <span className="sr-only">Actions</span>
              </TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading && Array.from(Array(10).keys()).map((x) => <ProfileUserRow key={x} isFetching />)}

            {!isLoading &&
              hasItems &&
              users.map((user) => (
                <ProfileUserRow
                  key={user.id}
                  user={user}
                  isFetching={isFetching || isChangingStatus}
                  isCurrentUser={user.userId === currentUserId}
                  onClickDelete={handleOpenDelete}
                  onStatusChange={handleStatusChange}
                  onResendInvite={handleResendInvite}
                />
              ))}
          </TableBody>
        </Table>
        <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
        </CardContent>
      </Card>

      {/* Invite Modal */}
      <ProfileUserInviteModal
        tenantId={tenantId}
        currentUserRole={tenant?.role}
        open={inviting}
        onClose={() => setInviting(false)}
        onSubmitted={() => {
          setInviting(false);
          refetch();
        }}
      />

      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <Modal open={!!deletingUser} onClose={handleCloseDelete} closeDisabled={isDeleting}>
          <ModalContent>
            <div className="uppercase text-xs font-semibold p-4 text-center">{tCommon('removeConfirmation')}</div>
            <Typography align="center" className="px-4 py-4">
              <Trans
                t={tCommon}
                i18nKey={'removeConfirm'}
                values={{ name: deletingUser?.name }}
                components={{ bold: <strong /> }}
              />
            </Typography>
          </ModalContent>
          <ModalFooter className="justify-center">
            <Button color="danger" onClick={handleDelete} progressing={isDeleting}>
              {tCommon('remove')}
            </Button>
            <Button color="primary-dark" onClick={handleCloseDelete}>
              {tCommon('cancel')}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};
